import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBooking } from '../context/BookingContext';
import { getPackages, searchAvailableVehicles } from '../services/vehicleService';
import { createBooking } from '../services/bookingService';
import RentalPackageCard from '../CabDetails/components/RentalPackageCard';
import VehicleCard from '../CabDetails/components/VehicleCard';
import FareSummary from './components/FareSummary';
import Payment from './components/Payment';
import { formatCurrency, formatDate, toApiDateTime } from '../utils/formatCurrency';
import { isAdvancePaymentRequired } from '../utils/bookingConfig';
import Loader from '../components/Loader';
import ErrorMessage from '../components/ErrorMessage';
import EmptyState from '../components/EmptyState';
import './Booking.css';

const STEPS = {
  RESULTS: 'results',
  PAYMENT: 'payment',
  SUCCESS: 'success',
};

const Booking = () => {
  const { booking, updateBooking } = useBooking();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bookingError, setBookingError] = useState('');
  const [bookingInProgress, setBookingInProgress] = useState(false);
  const [step, setStep] = useState(STEPS.RESULTS);
  const navigate = useNavigate();

  const isPackageMode =
    booking.tripType === 'rental' || booking.tripType === 'outstation';

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (isPackageMode) {
        const mode = booking.tripType === 'rental' ? 'rental' : 'outstation';
        const res = await getPackages(mode);
        const expectedTripType = mode === 'rental' ? 'rental' : booking.rideMode || 'one_way';
        setItems((res?.data || []).filter((pkg) =>
          pkg.package_type?.toLowerCase() === mode &&
          (mode !== 'outstation' || pkg.trip_type?.toLowerCase() === expectedTripType)
        ));
      } else {
        const payload = {
          pickup_location: booking.pickup.name,
          pickup_lat: booking.pickup.latitude,
          pickup_lng: booking.pickup.longitude,
          dropoff_location: booking.dropoff.name,
          dropoff_lat: booking.dropoff.latitude,
          dropoff_lng: booking.dropoff.longitude,
          date: booking.date,
          trip_type: booking.tripType === 'instant' ? 'one_way' : booking.tripType,
        };
        const res = await searchAvailableVehicles(payload);
        setItems(res?.data || []);
      }
    } catch (err) {
      console.error(err);
      setError('Could not fetch cabs. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [booking, isPackageMode]);

  useEffect(() => {
    if (!booking.pickup?.name) {
      navigate('/cab');
      return;
    }
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelect = (item) => {
    updateBooking('booking', null);
    updateBooking('payment', null);
    updateBooking('paymentVerification', null);
    if (isPackageMode) {
      updateBooking('selectedPackage', item);
      updateBooking('selectedVehicle', null);
    } else {
      updateBooking('selectedVehicle', item);
      updateBooking('selectedPackage', null);
    }
  };

  const handleBookNow = async () => {
    const selectedPackage = booking.selectedPackage;
    const selectedVehicle = booking.selectedVehicle;
    const isInstant = booking.tripType === 'instant';
    if ((isInstant ? !selectedVehicle : !selectedPackage) || bookingInProgress) return;

    if (!localStorage.getItem('accessToken')) {
      const returnPath = `${window.location.pathname}${window.location.search}`;
      try {
        sessionStorage.setItem('cabBookingLoginReturn', JSON.stringify(booking));
      } catch (err) {
        console.error('Could not save cab booking before login:', err);
        setBookingError('Could not save your booking. Please try again.');
        return;
      }
      navigate(`/signup?redirect=${encodeURIComponent(returnPath)}`);
      return;
    }

    setBookingError('');
    setBookingInProgress(true);
    try {
      if (isInstant) {
        const response = await createBooking({
          vehicle_type_id: selectedVehicle.vehicle_type_id,
          pickup_location_id: booking.pickup.id,
          drop_location_id: booking.dropoff.id,
          trip_type: 'one_way',
          pickup_date: booking.date,
          ...(booking.pickupTime && { pickup_time: booking.pickupTime }),
          ride_mode: 'normal',
        });
        if (response?.success === false) {
          throw new Error(response.message || 'Could not create your booking. Please try again.');
        }
        if (!response?.data?.booking_id && !response?.data?._id) {
          throw new Error(response?.message || 'Booking details could not be loaded. Please try again.');
        }

        updateBooking('booking', response.data);
        const totalAmount = Number(response.data.total_amount ?? selectedVehicle.total_price ?? 0);
        updateBooking('paymentVerification', {
          booking_id: response.data.booking_id || response.data._id,
          status: 'confirmed',
          payment_status: 'After Trip',
          payment_type: 'instant_confirmation',
          total_amount: totalAmount,
          advance_amount: 0,
          paid_amount: 0,
          remaining_amount: totalAmount,
        });
        setStep(STEPS.SUCCESS);
        return;
      }

      const payload = {
        pickup_location_id: booking.pickup?.id || null,
        drop_location_id: booking.dropoff?.id || null,
        pickup_date: toApiDateTime(booking.date),
        ...(booking.dropDate && { drop_date: toApiDateTime(booking.dropDate) }),
        ...(booking.returnDate && { return_date: toApiDateTime(booking.returnDate) }),
        ...(booking.pickupTime && { pickup_time: booking.pickupTime }),
        ...(booking.dropTime && { drop_time: booking.dropTime }),
        package_id: selectedPackage._id,
        vehicle_type_id: selectedPackage.vehicle_type_id || selectedPackage._id || null,
        ride_mode: booking.tripType,
        trip_type: booking.tripType === 'rental' ? '' : booking.rideMode || 'one_way',
      };
      const response = await createBooking(payload);
      if (!response?.data?.booking_id && !response?.data?._id) {
        throw new Error('Booking details could not be loaded. Please try again.');
      }

      const bookingData = response.data;
      updateBooking('booking', bookingData);

      if (!isAdvancePaymentRequired(booking.tripType)) {
        const totalAmount = Number(bookingData?.total_amount ?? bookingData?.advance_amount ?? 0);
        updateBooking('paymentVerification', {
          booking_id: bookingData?.booking_id || bookingData?._id,
          status: 'confirmed',
          payment_status: 'After Trip',
          payment_type: 'rental_confirmation',
          total_amount: totalAmount,
          advance_amount: 0,
          paid_amount: 0,
          remaining_amount: totalAmount,
        });
        setStep(STEPS.SUCCESS);
        return;
      }

      setStep(STEPS.PAYMENT);
    } catch (err) {
      console.error(err);
      setBookingError(err.message || 'Could not create your booking. Please try again.');
    } finally {
      setBookingInProgress(false);
    }
  };

  if (loading) return <div className="bk-shell"><Loader /></div>;

  if (error) {
    return (
      <div className="bk-shell">
        <ErrorMessage message={error} onRetry={fetchItems} />
      </div>
    );
  }

  if (step === STEPS.RESULTS) {
    const packageHeading = booking.tripType === 'instant'
      ? 'Available Cabs'
      : booking.tripType === 'rental'
        ? 'Rental Packages'
        : `Outstation ${booking.rideMode === 'round_trip' ? 'Round Trip' : 'One Way'} Packages`;

    return (
      <div className="bk-shell bk-results">
        <div className="bk-results-header">
          <div>
            <h1 className="bk-title">{packageHeading}</h1>
            <p className="bk-subtitle">
              {booking.pickup?.name}
              {booking.dropoff?.name ? ` → ${booking.dropoff.name}` : ''} ·{' '}
              {formatDate(booking.date)}
              {booking.tripType === 'instant' && booking.pickupTime
                ? ` at ${booking.pickupTime}`
                : ''}
            </p>
          </div>
          <button className="bk-back" onClick={() => navigate('/cab')}>
            ← Change
          </button>
        </div>

        <div className="bk-results-grid">
          <div className="bk-list">
            {items.length === 0 ? (
              <EmptyState
                title={isPackageMode ? 'No matching packages' : 'No available cabs'}
                description={
                  isPackageMode
                    ? `No ${packageHeading.toLowerCase()} are available for this trip.`
                    : 'No vehicles are available for this route and pickup time.'
                }
              />
            ) : (
              items.map((item, i) =>
                isPackageMode ? (
                  <RentalPackageCard
                    key={item._id || i}
                    pkg={item}
                    selected={booking.selectedPackage?._id === item._id}
                    onSelect={handleSelect}
                  />
                ) : (
                  <VehicleCard
                    key={item.vehicle_type_id || item._id || i}
                    vehicle={item}
                    onSelect={handleSelect}
                  />
                )
              )
            )}
          </div>
          <div className="bk-summary">
            <FareSummary />
            {(booking.selectedPackage || booking.selectedVehicle) && (
              <div className="bk-book-now-wrap">
                {bookingError && <p className="bk-book-error">{bookingError}</p>}
                <button
                  className="bk-btn bk-book-now"
                  disabled={bookingInProgress}
                  onClick={handleBookNow}
                >
                  {bookingInProgress
                    ? 'Creating booking...'
                    : isAdvancePaymentRequired(booking.tripType)
                      ? 'Continue to Payment'
                      : 'Book Now'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (step === STEPS.PAYMENT) {
    return (
      <div className="bk-shell">
        <Payment
          onSuccess={() => setStep(STEPS.SUCCESS)}
          onBack={() => setStep(STEPS.RESULTS)}
        />
      </div>
    );
  }

  if (step === STEPS.SUCCESS) {
    const verifiedPayment = booking.paymentVerification || {};
    const bookingId = verifiedPayment.booking_id || booking.booking?.booking_id || booking.booking?._id;

    return (
      <div className="bk-shell bk-success">
        <div className="bk-success-card">
          <div className="bk-check">✓</div>
          <h1>{verifiedPayment.status === 'confirmed' ? 'Booking Confirmed' : 'Payment Successful'}</h1>
          <p>
            {booking.tripType === 'rental'
              ? 'Your rental booking is confirmed. Payment is due after your trip is completed.'
              : booking.tripType === 'instant'
                ? 'Your instant cab booking is confirmed. Payment is due after your ride is completed.'
                : 'Your advance payment has been verified.'}
          </p>
          <div className="bk-success-meta">
            <div><span>Booking ID</span><strong>{bookingId || '—'}</strong></div>
            <div><span>Pickup</span><strong>{booking.pickup?.name}</strong></div>
            {booking.dropoff?.name && (
              <div><span>Drop</span><strong>{booking.dropoff.name}</strong></div>
            )}
            <div>
              <span>{booking.tripType === 'instant' ? 'Vehicle' : 'Package'}</span>
              <strong>
                {booking.selectedVehicle?.vehicle_type || booking.selectedPackage?.vehicle_type || '—'}
              </strong>
            </div>
            <div><span>Date</span><strong>{formatDate(booking.date)}</strong></div>
            <div><span>Booking status</span><strong>{verifiedPayment.status || '—'}</strong></div>
            {booking.tripType === 'rental' || booking.tripType === 'instant' ? (
              <div className="bk-success-payment-note">
                <span>Payment</span>
                <strong>
                  Due after {booking.tripType === 'instant' ? 'ride' : 'trip'} completion
                </strong>
              </div>
            ) : (
              <>
                <div><span>Payment status</span><strong>{verifiedPayment.payment_status || '—'}</strong></div>
                <div><span>Payment type</span><strong>{verifiedPayment.payment_type || '—'}</strong></div>
                <div><span>Total fare</span><strong>{formatCurrency(verifiedPayment.total_amount)}</strong></div>
                <div><span>Advance amount</span><strong>{formatCurrency(verifiedPayment.advance_amount)}</strong></div>
                <div><span>Paid</span><strong>{formatCurrency(verifiedPayment.paid_amount ?? verifiedPayment.amount)}</strong></div>
                <div><span>Remaining</span><strong>{formatCurrency(verifiedPayment.remaining_amount)}</strong></div>
              </>
            )}
          </div>
          <button
            className="bk-btn"
            onClick={() => {
              navigate('/cab');
            }}
            style={{ margin: '1rem' }}
          >
            Book Another Ride
          </button>
          <button
            className="bk-btn"
            onClick={() => {
              navigate('/profile/cab');
            }}
          >
            Go to My Bookings
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default Booking;